import { ResponsivePie } from "@nivo/pie";
import { PHASE_ORDER, TOOLTIP_STYLE, insightsTheme, useChartPalette, type Phase } from "./insights/theme";

// The org-view spec-phase donut (feedback): the CURRENT distribution of the org's
// specs by status (draft/specify/build/verify/done). Point-in-time, not over time
// — the schema keeps only current status. Uses the std-27 phase hues and the same
// donut treatment as the product's verification chart; the center carries the
// total spec count.

export type Phases = Record<Phase, number>;

export function SpecPhaseDonut({ phases }: { phases: Phases }) {
  const palette = useChartPalette();
  const total = PHASE_ORDER.reduce((s, p) => s + (phases[p] ?? 0), 0);
  const data = PHASE_ORDER.map((p) => ({ id: p, value: phases[p] ?? 0 })).filter((d) => d.value > 0);

  if (total === 0) {
    return (
      <div
        data-testid="spec-phase-donut"
        className="flex h-64 items-center justify-center rounded-xl border border-edge bg-surface text-sm text-muted"
      >
        No specs yet.
      </div>
    );
  }

  return (
    <div className="relative h-64 rounded-xl border border-edge bg-surface p-2" data-testid="spec-phase-donut">
      <ResponsivePie
        data={data}
        margin={{ top: 24, right: 96, bottom: 24, left: 96 }}
        colors={(d) => palette.phase[d.id as Phase]}
        theme={insightsTheme}
        innerRadius={0.7}
        padAngle={1.5}
        cornerRadius={4}
        activeOuterRadiusOffset={6}
        enableArcLabels={false}
        arcLinkLabel={(d) => `${d.id} (${d.value})`}
        arcLinkLabelsColor={{ from: "color" }}
        arcLinkLabelsTextColor="rgb(var(--color-text-secondary, 100 116 139))"
        arcLinkLabelsThickness={1.5}
        tooltip={({ datum }) => (
          <div className="rounded-lg px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
            <span className="font-medium">{datum.value}</span> of {total} specs {String(datum.id)}
          </div>
        )}
        animate
      />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-semibold text-heading">{total}</div>
        <div className="text-xs text-muted">specs</div>
      </div>
    </div>
  );
}
